/**
 * Per-lesson Practice tab.
 *
 * Two surfaces, each a self-contained runner with concept-aggregated FSRS
 * grading and localStorage-backed resume:
 *   • Flashcards - Anki-style rate-to-advance review (LessonFlashcardRunner).
 *   • Quiz       - stored-question runner (LessonQuizRunner).
 *
 * Both are on-demand: `StepGate` gates each behind a "Generate" CTA when the
 * artifact hasn't been generated yet, then the runner polls/loads until ready.
 */

import { TabbedInterface } from '@ai-tutor/ui';
import { BookCheck, WalletCards } from 'lucide-react';
import { useLessonTabParam } from '@/hooks/lesson/useLessonTabParam';
import { useQuery } from '@tanstack/react-query';
import { mockFlashcards, type Flashcard } from '@/data/fixtures';
import { lessonApi } from '@/lib/api-client';
import type {
  ConceptInLesson,
  FlashcardItem,
  LessonContext,
} from '@/lib/api-client';
import type { StepView } from '@/types/pipeline';
import { ComingSoon } from '@/components/core/common/feedback/ComingSoon';
import { PanelBoundary } from '@/components/core/common/boundaries/PanelBoundary';
import { StepGate } from '../common/StepGate';
import { ActivityTrackedTab } from '../common/ActivityTrackedTab';
import { QuizSkeleton, FlashcardsSkeleton } from '../skeletons/PanelSkeletons';
import { useDataMode } from '@/contexts/DataModeContext';
import {
  useFlashcards,
  useGenerateContent,
  useQuiz,
  useRetryContent,
} from '@/hooks/lesson/useOnDemandContent';
import { LessonQuizRunner } from './LessonQuizRunner';
import { LessonFlashcardRunner } from './LessonFlashcardRunner';
import { queryKeys } from '@/config/query-client';

interface PracticeViewerProps {
  lessonId?: string;
  lessonStyle?: any;
  onProgressUpdate?: (flashcards: any, quiz: any, attempts: any) => void;
  /** Per-step view map; each `<StepGate>` reads `steps[step]`. */
  steps?: Record<string, StepView>;
  /** Lesson-level lifecycle terminal flag - used by off-flow gating. */
  lessonDone?: boolean;
  /**
   * True once the page has both lesson metadata + pipeline-status
   * snapshot loaded. Threaded into each `<StepGate>` so quiz /
   * flashcards show a neutral "Loading X" skeleton during the
   * initial-load gap instead of flashing "Waiting" or Generate CTA.
   */
  lessonReady?: boolean;
}

/** Map an API flashcard payload to the local shape used by the runner. */
function mapApiFlashcard(card: FlashcardItem, index: number): Flashcard {
  return {
    id: `fc-${index}`,
    front: card.front,
    back: card.back,
    difficulty: card.difficulty,
    mastered: false,
    concept_id: card.concept_id ?? null,
    frontVisual: card.front_visual ?? undefined,
    backVisual: card.back_visual ?? undefined,
    fromVisual: card.from_visual ?? false,
  };
}

const PRACTICE_VIEWS = ['flashcards', 'quiz'] as const;

export function PracticeViewer({
  lessonId,
  lessonStyle: __,
  onProgressUpdate: ___,
  steps,
  lessonDone,
  lessonReady,
}: PracticeViewerProps) {
  const { isMock, isApi, isEmpty } = useDataMode();

  // Lesson context - concepts power per-item concept tags + the crosswalk to
  // canonical mem0_memory_id for FSRS grading (quiz + flashcards).
  const contextQuery = useQuery({
    queryKey: queryKeys.lessons.context(lessonId || ''),
    queryFn: () => lessonApi.getLessonContext(lessonId as string),
    enabled: !!lessonId && isApi,
    staleTime: 30 * 1000,
  });
  const concepts: ConceptInLesson[] =
    (contextQuery.data as LessonContext | undefined)?.concepts ?? [];

  // API hooks
  const quiz = useQuiz(isApi ? lessonId : undefined);
  const flashcardsApi = useFlashcards(isApi ? lessonId : undefined);
  const generateContent = useGenerateContent(lessonId || '');
  const retryContent = useRetryContent(lessonId || '');

  // `?view=` — see `useLessonTabParam`.
  const [activeTab, setActiveTab] = useLessonTabParam(
    'view',
    PRACTICE_VIEWS,
    'flashcards'
  );

  const flashcardsMock: Flashcard[] = isMock ? mockFlashcards : [];
  const flashcards: Flashcard[] = isApi
    ? flashcardsApi.cards.map(mapApiFlashcard)
    : flashcardsMock;

  const flashcardRunner = (
    <LessonFlashcardRunner
      cards={flashcards}
      concepts={concepts}
      lessonId={lessonId}
      isLoading={
        flashcardsApi.isLoading ||
        flashcardsApi.status === 'pending' ||
        flashcardsApi.status === 'processing'
      }
      onTakeQuiz={() => setActiveTab('quiz')}
    />
  );

  // ---- Flashcards content (route by data mode) ----
  const flashcardsContent = isEmpty ? (
    <ComingSoon
      icon={WalletCards}
      title='Smart Flashcards'
      description='Review key concepts with spaced repetition flashcards.'
    />
  ) : isApi ? (
    <StepGate
      step='flashcards'
      lessonId={lessonId}
      steps={steps}
      lessonDone={lessonDone}
      lessonReady={lessonReady}
      skeleton={<FlashcardsSkeleton />}
      isGenerating={
        generateContent.pendingType === 'flashcards' ||
        retryContent.pendingType === 'flashcards'
      }
      generate={{
        icon: WalletCards,
        label: 'flashcards',
        description:
          'Generate flashcards from this lesson and review key concepts.',
        onGenerate: () => generateContent.mutate('flashcards'),
      }}
      onRetry={() => retryContent.mutate('flashcards')}
    >
      {flashcardRunner}
    </StepGate>
  ) : (
    flashcardRunner
  );

  // ---- Quiz content ----
  const quizRunner = (
    <LessonQuizRunner
      questions={quiz.questions}
      concepts={concepts}
      lessonId={lessonId}
      isLoading={
        quiz.isLoading ||
        quiz.status === 'pending' ||
        quiz.status === 'processing'
      }
      // Retry re-runs a failed step, or regenerates an empty quiz (`retry_step`).
      onRegenerate={() => retryContent.mutate('quiz')}
      regenerating={retryContent.pendingType === 'quiz'}
      onPractiseFlashcards={() => setActiveTab('flashcards')}
    />
  );

  const quizContent = isEmpty ? (
    <ComingSoon
      icon={BookCheck}
      title='Interactive Quizzes'
      description='Test your knowledge with AI-generated quizzes tailored to your lesson.'
    />
  ) : isApi ? (
    <StepGate
      step='quiz'
      lessonId={lessonId}
      steps={steps}
      lessonDone={lessonDone}
      lessonReady={lessonReady}
      skeleton={<QuizSkeleton />}
      isGenerating={
        generateContent.pendingType === 'quiz' ||
        retryContent.pendingType === 'quiz'
      }
      generate={{
        icon: BookCheck,
        label: 'quiz',
        description:
          'Generate a concept-tagged quiz for this lesson. Each question is graded into your daily revision plan.',
        onGenerate: () => generateContent.mutate('quiz'),
      }}
      onRetry={() => retryContent.mutate('quiz')}
    >
      {quizRunner}
    </StepGate>
  ) : (
    quizRunner
  );

  // Each sub-tab gets its own PanelBoundary so a crash in one doesn't take
  // down the other. The `key` forces a remount on tab switch (the runners'
  // own state survives via localStorage-backed sessions).
  const tabs = [
    {
      id: 'flashcards',
      label: 'Flashcards',
      icon: WalletCards,
      count: flashcards.length || undefined,
      content: (
        <PanelBoundary key='flashcards' name='Flashcards'>
          <ActivityTrackedTab
            contentType='practice_flashcard'
            lessonId={lessonId}
          >
            {flashcardsContent}
          </ActivityTrackedTab>
        </PanelBoundary>
      ),
    },
    {
      id: 'quiz',
      label: 'Quiz',
      icon: BookCheck,
      count: quiz.questions.length || undefined,
      content: (
        <PanelBoundary key='quiz' name='Quiz'>
          <ActivityTrackedTab contentType='practice_quiz' lessonId={lessonId}>
            {quizContent}
          </ActivityTrackedTab>
        </PanelBoundary>
      ),
    },
  ];

  return (
    <div className='flex h-full flex-col'>
      <TabbedInterface
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={tab =>
          setActiveTab(tab as (typeof PRACTICE_VIEWS)[number])
        }
        variant='pills'
        className='flex-1'
        showCounts={false}
      />
    </div>
  );
}
